import { UsageResponse } from './api';

/**
 * Where the demo key lives.
 *
 * localStorage, per the brief. Stating the trade-off plainly rather than
 * pretending it away: anything running on this origin can read it, so a key here
 * is only as safe as the page. That is acceptable for a disposable demo tenant
 * whose index holds synthetic listings and which is suspended when the
 * evaluation ends — and it would NOT be acceptable for a production key, which
 * is why the operator docs say to issue a scoped one for real integrations.
 *
 * sessionStorage was the alternative and is worse here: a prospect who closes
 * the tab and comes back should not have to find the email with the key in it.
 */
const KEY_STORAGE = 'propertystack.demo.key';
const TENANT_STORAGE = 'propertystack.demo.tenant';

export interface Session {
  key: string;
  tenant: UsageResponse['tenant'];
  documents: UsageResponse['documents'];
}

/** Every accessor is wrapped: Safari in private mode throws on localStorage
 *  access rather than returning null, and an uncaught throw here would blank the
 *  page before the key form ever rendered. */
function safeGet(name: string): string | null {
  try {
    return window.localStorage.getItem(name);
  } catch {
    return null;
  }
}

function safeSet(name: string, value: string): void {
  try {
    window.localStorage.setItem(name, value);
  } catch {
    /* storage unavailable — the session simply does not persist */
  }
}

function safeRemove(name: string): void {
  try {
    window.localStorage.removeItem(name);
  } catch {
    /* nothing to do */
  }
}

export function load(): Session | null {
  const key = safeGet(KEY_STORAGE);
  const raw = safeGet(TENANT_STORAGE);
  if (!key || !raw) return null;
  try {
    const parsed = JSON.parse(raw) as { tenant: Session['tenant']; documents: Session['documents'] };
    if (!parsed?.tenant?.slug) return null;
    return { key, tenant: parsed.tenant, documents: parsed.documents };
  } catch {
    // A half-written or stale entry is cleared rather than tolerated: the app
    // would otherwise show a signed-in shell that every API call then rejects.
    clear();
    return null;
  }
}

export function save(key: string, usage: UsageResponse): Session {
  safeSet(KEY_STORAGE, key);
  safeSet(TENANT_STORAGE, JSON.stringify({ tenant: usage.tenant, documents: usage.documents }));
  return { key, tenant: usage.tenant, documents: usage.documents };
}

export function clear(): void {
  safeRemove(KEY_STORAGE);
  safeRemove(TENANT_STORAGE);
}
